import { useCallback, useEffect, useState } from 'react';

import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  RECENT_SEARCHES_KEY,
  removeRecentSearch,
} from '@/lib/search-history';

export interface UseRecentSearches {
  recent: string[];
  add: (query: string) => void;
  remove: (query: string) => void;
  clear: () => void;
}

/**
 * React view over the localStorage search history. Starts empty for a clean
 * SSR/first-paint match, then hydrates on mount and stays in sync across tabs
 * via the `storage` event.
 */
export function useRecentSearches(): UseRecentSearches {
  const [recent, setRecent] = useState<string[]>([]);

  useEffect(() => {
    setRecent(getRecentSearches());
    const onStorage = (event: StorageEvent): void => {
      if (event.key === RECENT_SEARCHES_KEY) setRecent(getRecentSearches());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const add = useCallback((query: string): void => {
    setRecent(addRecentSearch(query));
  }, []);
  const remove = useCallback((query: string): void => {
    setRecent(removeRecentSearch(query));
  }, []);
  const clear = useCallback((): void => {
    setRecent(clearRecentSearches());
  }, []);

  return { recent, add, remove, clear };
}

export default useRecentSearches;
